import { useNavigate } from 'react-router-dom';
import { PortalShell } from '@/portal/components/PortalShell';
import { Ico } from '@/portal/components/Icons';
import { statusPill } from '@/portal/components/ui';
import { usePortalData } from '@/portal/data/PortalDataContext';
import { productPhotoUrl } from '@/portal/api/client';
import { relativeTime } from '@/portal/data/time';
import type { Portfolio } from '@/portal/api/types';

function coverUrl(pf: Portfolio): string | null {
  const code = pf.cover_code || pf.item_codes[0];
  return code ? productPhotoUrl(code) : null;
}

export function PortfoliosPage({ scope = 'mine' }: { scope?: 'mine' | 'shared' }) {
  const { portfolios, portfoliosLoading, createPortfolio } = usePortalData();
  const navigate = useNavigate();

  const list = scope === 'shared' ? [] : portfolios;
  const title = scope === 'shared' ? 'Shared With Me' : 'My Portfolios';

  const onCreate = async () => {
    const pf = await createPortfolio({ title: 'Untitled portfolio' });
    navigate(`/portal/portfolio/${pf.id}`);
  };

  return (
    <PortalShell title={title}>
      {scope !== 'shared' && (
        <div className="pl-note-banner">
          <Ico.info width={17} height={17} />
          <span>
            Build a curated presentation of products for a client or event. Add items from your collection,
            arrange them, then present live, share a link, or export a PDF.
          </span>
        </div>
      )}

      <div className="pl-cards">
        {scope !== 'shared' && (
          <button className="pl-new-card" onClick={onCreate}>
            <Ico.plus width={26} height={26} />
            New portfolio
          </button>
        )}

        {list.map((pf) => {
          const cover = coverUrl(pf);
          return (
            <button key={pf.id} className="pl-pf-card" onClick={() => navigate(`/portal/portfolio/${pf.id}`)}>
              <div
                className="pl-pf-cover"
                style={cover ? { backgroundImage: `url(${cover})` } : undefined}
              >
                {statusPill(pf.status)}
                <span className="pl-pf-count">{pf.item_count} items</span>
              </div>
              <div className="pl-pf-body">
                <h3>{pf.title}</h3>
                <div className="client">{pf.client || '—'}</div>
                <div className="meta">
                  <span>{relativeTime(pf.updated_at)}</span>
                </div>
              </div>
            </button>
          );
        })}

        {scope === 'shared' && (
          <div className="pl-empty" style={{ gridColumn: '1 / -1' }}>
            Nothing has been shared with you yet.
          </div>
        )}
        {scope === 'mine' && !portfoliosLoading && list.length === 0 && (
          <div className="pl-empty" style={{ gridColumn: '2 / -1' }}>
            No portfolios yet. Create one to get started.
          </div>
        )}
      </div>
    </PortalShell>
  );
}
